/**
 * WEB-925 — treat a missing tickets resource as an honest empty list.
 *
 * Prod `GET /users/me/tickets` currently 404s when the user has no tickets (or
 * the route is absent). RTK Query rejects that, and `@loci/store`'s error
 * middleware maps a non-numeric status (`PARSING_ERROR` on a non-JSON 404
 * body) to the "Connection Error" toast. Leadbot owns flipping the API to
 * `200 + []`; this helper is the web companion so the Profile Ticket History
 * section degrades to the empty tombstone either way.
 */

import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import type { UserTicketsResponse } from '@loci/types';

const DEFAULT_LIMIT = 3;

export function isHonestEmptyTicketsError(error: unknown): boolean {
  if (error === null || typeof error !== 'object' || !('status' in error)) {
    return false;
  }
  const status = (error as { status: unknown }).status;
  if (status === 404 || status === '404') {
    return true;
  }
  if (status !== 'PARSING_ERROR' || !('originalStatus' in error)) {
    return false;
  }
  const originalStatus = (error as { originalStatus: unknown }).originalStatus;
  return originalStatus === 404 || originalStatus === '404';
}

export function emptyTicketsResponse(arg?: {
  limit?: number;
  offset?: number;
}): UserTicketsResponse {
  return {
    tickets: [],
    pagination: {
      total: 0,
      limit: arg?.limit ?? DEFAULT_LIMIT,
      offset: arg?.offset ?? 0,
      hasMore: false,
    },
  };
}

export function resolveUserTicketsQueryResult(
  result: { data?: unknown; error?: FetchBaseQueryError },
  arg?: { limit?: number; offset?: number },
): { data: UserTicketsResponse } | { error: FetchBaseQueryError } {
  if (result.error) {
    if (isHonestEmptyTicketsError(result.error)) {
      return { data: emptyTicketsResponse(arg) };
    }
    return { error: result.error };
  }
  return { data: result.data as UserTicketsResponse };
}
